/**
 * FieldVisibilityRulesSection - Clara Vale grouped accordion view.
 *
 * Groups rules by claim type (derived from `key` prefix), renders each
 * group as a collapsible accordion with white cards on cream, colored
 * effect badges, and plain-English condition summaries.
 */

import { useState, useMemo } from 'react'
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  ChevronDown,
  CircleCheck,
  CircleMinus,
  Package,
  Wrench,
  CircleAlert,
  Home,
  Search,
  type LucideIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import { FeatureGate } from '@/admin/components/feature-gate'
import { FLAGS } from '@/admin/hooks/use-feature-flag'
import {
  useFieldVisibilityRules,
  useDeleteFieldVisibilityRule,
  detectGroup,
  REGISTRATION_GROUPS,
  CLAIM_GROUPS,
  type FieldVisibilityRule,
  type FieldVisibilityEffect,
} from '@/admin/hooks/use-field-visibility-rules'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import { FieldVisibilityRuleDialog } from './field-visibility-rule-dialog'
import { cn } from '@/shared/lib/utils'

import { toastApiError } from '@/shared/lib/api-error'

// ---------------------------------------------------------------------------
// Group config - claim type detection from rule `key`
// ---------------------------------------------------------------------------

type ClaimGroupConfig = {
  id: string
  label: string
  icon: LucideIcon
  iconClass: string
  badgeClass: string
  keyPrefix: string
}

const GROUP_CONFIG: ClaimGroupConfig[] = [
  {
    id: 'host',
    label: 'Host Fields',
    icon: Home,
    iconClass: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
    badgeClass: '',
    keyPrefix: '__host__',
  },
  {
    id: 'installed',
    label: 'Installed Claims',
    icon: CircleCheck,
    iconClass: 'bg-[var(--green-bg)] text-[var(--green)]',
    badgeClass: '',
    keyPrefix: 'claim_field_visible_',
  },
  {
    id: 'job_stop',
    label: 'Job Stop Claims',
    icon: CircleMinus,
    iconClass: 'bg-[rgba(234,88,12,0.08)] text-[#9a3412]',
    badgeClass: '',
    keyPrefix: 'job_stop_field_visible_',
  },
  {
    id: 'uninstalled',
    label: 'Uninstalled Claims',
    icon: Package,
    iconClass: 'bg-[rgba(37,99,235,0.08)] text-[#1e40af]',
    badgeClass: '',
    keyPrefix: 'uninstalled_field_visible_',
  },
  {
    id: 'workmanship',
    label: 'Workmanship Claims',
    icon: Wrench,
    iconClass: 'bg-[rgba(124,58,237,0.08)] text-[#6b21a8]',
    badgeClass: '',
    keyPrefix: 'workmanship_field_visible_',
  },
  {
    id: 'other',
    label: 'Other Rules',
    icon: CircleAlert,
    iconClass: 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]',
    badgeClass: '',
    keyPrefix: '__other__',
  },
]

// ---------------------------------------------------------------------------
// Effect badge styling
// ---------------------------------------------------------------------------

const EFFECT_CONFIG: Record<
  FieldVisibilityEffect | string,
  { label: string; className: string }
> = {
  show: {
    label: 'Editable',
    className:
      'bg-[rgba(37,99,235,0.08)] text-[#1e40af] border-transparent',
  },
  require: {
    label: 'Required',
    className:
      'bg-[var(--green-bg)] text-[var(--green)] border-transparent',
  },
  hide: {
    label: 'Hidden',
    className:
      'bg-[var(--status-warn-bg)] text-[var(--status-warn)] border-transparent',
  },
  optional: {
    label: 'Read-only',
    className:
      'bg-[rgba(124,58,237,0.08)] text-[#6b21a8] border-transparent',
  },
}

const EFFECT_DOT: Record<string, string> = {
  show: 'bg-[rgba(37,99,235,0.5)]',
  require: 'bg-[rgba(28,90,72,0.5)]',
  hide: 'bg-[rgba(146,64,14,0.5)]',
  optional: 'bg-[rgba(107,33,168,0.5)]',
}

// ---------------------------------------------------------------------------
// Condition → plain English
// ---------------------------------------------------------------------------

const STATE_LABELS: Record<string, string> = {
  open: 'Draft',
  draft: 'Draft',
  submitted: 'Submitted',
  awaiting_docs: 'Awaiting Docs',
  intake_review: 'Intake Review',
  under_review: 'Under Review',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  settlement: 'Settlement',
  closed: 'Closed',
  rejected: 'Rejected',
  lab_analysis: 'Lab Analysis',
  inspection: 'Inspection',
  loa_pending: 'LOA Pending',
  loa_approved: 'LOA Approved',
}

function humaniseCondition(conditions: PredicateNode | null): string {
  if (!conditions) return 'Always'
  if (conditions.type === 'group' && conditions.children?.length) {
    const parts: string[] = []
    for (const child of conditions.children) {
      if (child.type === 'clause') {
        const ref = child.ref ?? ''
        if (ref.includes('current_state') && child.op === 'in' && Array.isArray(child.value)) {
          const states = (child.value as string[]).map(
            (s) => STATE_LABELS[s] ?? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          )
          if (states.length <= 3) {
            parts.push(`In ${states.join(', ')}`)
          } else {
            parts.push(`In ${states.slice(0, 2).join(', ')} +${states.length - 2} more`)
          }
        } else if (ref.includes('current_state') && child.op === 'not_in' && Array.isArray(child.value)) {
          parts.push('When state is not in specific states')
        } else if (child.op === 'eq') {
          const fieldName = ref.split('.').pop()?.replace(/_/g, ' ') ?? ref
          const val = typeof child.value === 'string' ? child.value : String(child.value ?? '')
          parts.push(`When ${fieldName} is "${val}"`)
        } else if (child.op === 'ne') {
          const fieldName = ref.split('.').pop()?.replace(/_/g, ' ') ?? ref
          const val = typeof child.value === 'string' ? child.value : String(child.value ?? '')
          parts.push(`When ${fieldName} is not "${val}"`)
        }
      }
    }
    return parts.length > 0 ? parts.join(' and ') : 'Custom condition'
  }
  return 'Custom condition'
}

// ---------------------------------------------------------------------------
// Humanise field name from code_name
// ---------------------------------------------------------------------------

function humaniseFieldName(displayName: string, codeName: string): string {
  if (displayName && displayName !== codeName) return displayName
  return codeName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

// ---------------------------------------------------------------------------
// Filter pills
// ---------------------------------------------------------------------------

type FilterKey = 'all' | 'show' | 'require' | 'hide' | 'optional'

const FILTER_OPTIONS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'show', label: 'Editable' },
  { key: 'require', label: 'Required' },
  { key: 'hide', label: 'Hidden' },
  { key: 'optional', label: 'Read-only' },
]

// ---------------------------------------------------------------------------
// Effect sub-groups within each claim type
// ---------------------------------------------------------------------------

const EFFECT_ORDER: FieldVisibilityEffect[] = ['show', 'require', 'hide', 'optional']

const EFFECT_ACCENT: Record<string, string> = {
  show: 'border-l-[rgba(37,99,235,0.4)]',
  require: 'border-l-[rgba(28,90,72,0.5)]',
  hide: 'border-l-[rgba(146,64,14,0.4)]',
  optional: 'border-l-[rgba(107,33,168,0.4)]',
}

function EffectSubgroups({
  rules,
  onEdit,
  onDelete,
}: {
  rules: FieldVisibilityRule[]
  onEdit: (rule: FieldVisibilityRule) => void
  onDelete: (rule: FieldVisibilityRule) => void
}) {
  const byEffect = useMemo(() => {
    const map = new Map<string, FieldVisibilityRule[]>()
    for (const r of rules) {
      const list = map.get(r.effect) ?? []
      list.push(r)
      map.set(r.effect, list)
    }
    return EFFECT_ORDER.filter((e) => map.has(e)).map((e) => ({
      effect: e,
      config: EFFECT_CONFIG[e],
      rules: map.get(e)!,
    }))
  }, [rules])

  return (
    <div className="divide-y-[0.5px] divide-[var(--rule)]">
      {byEffect.map(({ effect, config, rules: effectRules }) => (
        <div key={effect}>
          {/* Effect sub-header */}
          <div className="flex items-center gap-2 bg-[var(--bg-subtle)] px-4 py-2">
            <span
              className={cn(
                'h-[6px] w-[6px] rounded-full',
                EFFECT_DOT[effect],
              )}
            />
            <span className="text-[10px] font-normal uppercase tracking-[0.12em] text-[var(--ink-3)]">
              {config.label}
            </span>
            <span className="text-[10px] font-light text-[var(--ink-3)]">
              {effectRules.length}
            </span>
          </div>
          {/* Field rows */}
          <div className="divide-y-[0.5px] divide-[var(--rule)]">
            {effectRules.map((rule) => (
              <div
                key={rule.uuid}
                role="button"
                tabIndex={0}
                onClick={() => onEdit(rule)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onEdit(rule)
                  }
                }}
                className={cn(
                  'group flex cursor-pointer items-center justify-between border-l-[2.5px] bg-[var(--bg-light)] px-4 py-2.5 transition-colors duration-150 hover:bg-[var(--bg)]',
                  EFFECT_ACCENT[effect],
                )}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="text-[12.5px] font-normal text-[var(--ink)]">
                    {humaniseFieldName(
                      rule.target_field_display_name,
                      rule.target_field_code_name,
                    )}
                  </span>
                  {!rule.is_active && (
                    <span className="shrink-0 rounded-[3px] bg-[var(--bg-subtle)] px-1.5 py-px text-[9px] font-normal uppercase tracking-[0.06em] text-[var(--ink-3)]">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-light text-[var(--ink-3)]">
                    {humaniseCondition(rule.conditions)}
                  </span>
                  {/* Hover actions */}
                  <div className="flex items-center gap-0.5 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        onEdit(rule)
                      }}
                      title="Edit"
                      className="h-6 w-6 text-[var(--ink-3)] hover:text-[var(--green)]"
                    >
                      <Pencil size={11} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDelete(rule)
                      }}
                      title="Delete"
                      className="h-6 w-6 text-[var(--ink-3)] hover:text-[var(--status-error)]"
                    >
                      <Trash2 size={11} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main exports
// ---------------------------------------------------------------------------

type Scope = 'registration' | 'claims'

export function ClaimFieldVisibilityRulesSection() {
  return (
    <FeatureGate flag={FLAGS.REGISTRATION_RULES}>
      <Section scope="claims" />
    </FeatureGate>
  )
}


function Section({ scope }: { scope: Scope }) {
  const { data: rules, isLoading } = useFieldVisibilityRules()
  const remove = useDeleteFieldVisibilityRule()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<FieldVisibilityRule | null>(null)
  const [deleting, setDeleting] = useState<FieldVisibilityRule | null>(null)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(['host', 'installed']),
  )
  const [filter, setFilter] = useState<FilterKey>('all')
  const [search, setSearch] = useState('')

  function openCreate() {
    setEditing(null)
    setDialogOpen(true)
  }

  function openEdit(rule: FieldVisibilityRule) {
    setEditing(rule)
    setDialogOpen(true)
  }

  async function handleDelete() {
    if (!deleting) return
    try {
      await remove.mutateAsync(deleting.uuid)
      toast.success('Rule deleted')
      setDeleting(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete rule')
    }
  }

  function toggleGroup(id: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const scopeGroups = scope === 'registration' ? REGISTRATION_GROUPS : CLAIM_GROUPS

  // Group and filter rules
  const { groups, effectCounts, totalCount } = useMemo(() => {
    const allRules = (rules ?? []).filter((r) => scopeGroups.has(detectGroup(r)))
    const searchLower = search.toLowerCase()

    const filtered = allRules.filter((r) => {
      if (filter !== 'all' && r.effect !== filter) return false
      if (
        searchLower &&
        !r.target_field_display_name.toLowerCase().includes(searchLower) &&
        !r.target_field_code_name.toLowerCase().includes(searchLower) &&
        !r.name.toLowerCase().includes(searchLower)
      )
        return false
      return true
    })

    const grouped = new Map<string, FieldVisibilityRule[]>()
    for (const rule of filtered) {
      const groupId = detectGroup(rule)
      const list = grouped.get(groupId) ?? []
      list.push(rule)
      grouped.set(groupId, list)
    }

    const counts: Record<string, number> = {}
    for (const r of allRules) {
      counts[r.effect] = (counts[r.effect] ?? 0) + 1
    }

    return {
      groups: GROUP_CONFIG.filter((g) => scopeGroups.has(g.id) && grouped.has(g.id)).map((g) => ({
        ...g,
        rules: grouped.get(g.id)!,
      })),
      effectCounts: counts,
      totalCount: allRules.length,
    }
  }, [rules, filter, search, scopeGroups])

  const sectionTitle = 'Field Visibility Rules'
  const sectionDesc =
    scope === 'registration'
      ? 'Control when host fields appear, hide, or become required during registration.'
      : 'Control when claim fields appear, hide, or become required - based on claim type and workflow state.'

  if (isLoading) {
    return (
      <div className="mb-8">
        <p className="clara-section-label">{sectionTitle}</p>
        <p className="clara-small mb-4 mt-1">{sectionDesc}</p>
        <div className="flex items-center justify-center rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg)] py-10">
          <Loader2 className="animate-spin text-[var(--ink-3)]" size={18} />
        </div>
      </div>
    )
  }

  if (totalCount === 0) {
    return (
      <div className="mb-8">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <p className="clara-section-label">{sectionTitle}</p>
            <p className="clara-small mt-1">{sectionDesc}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={openCreate}
            className="text-[11px]"
          >
            <Plus size={12} className="mr-1" />
            Add rule
          </Button>
        </div>
        <div className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-6 text-center text-[12px] text-[var(--ink-3)]">
          No field visibility rules yet.
        </div>
        <FieldVisibilityRuleDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          rule={editing}
        />
      </div>
    )
  }

  return (
    <div className="mb-8" data-tour="field-visibility-rules">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <p className="clara-section-label">{sectionTitle}</p>
          <p className="clara-small mt-1">{sectionDesc}</p>
        </div>
        <Button
          type="button"
          onClick={openCreate}
          className="h-8 rounded-md bg-[var(--green)] px-4 text-[11px] font-normal text-white hover:bg-[var(--green-hover)]"
        >
          <Plus size={12} className="mr-1.5" />
          Add rule
        </Button>
      </div>

      {/* Toolbar: search + filter pills */}
      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <div className="relative">
          <Search
            size={13}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-3)]"
          />
          <Input
            type="text"
            placeholder="Search fields..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-[180px] rounded-md border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] pl-8 text-[11px] font-light"
          />
        </div>
        <div className="flex gap-1.5">
          {FILTER_OPTIONS.map((opt) => {
            const isActive = filter === opt.key
            const count =
              opt.key === 'all'
                ? totalCount
                : (effectCounts[opt.key] ?? 0)
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setFilter(opt.key)}
                className={cn(
                  'rounded-full border-[0.5px] px-3 py-1 text-[10.4px] font-light transition-all duration-150',
                  isActive
                    ? 'border-[rgba(28,90,72,0.2)] bg-[var(--green-bg)] font-normal text-[var(--green)]'
                    : 'border-[var(--rule-strong)] bg-[var(--bg)] text-[var(--ink-2)] hover:border-[rgba(28,90,72,0.15)] hover:bg-[var(--green-bg)] hover:text-[var(--green)]',
                )}
              >
                {opt.label}
                <span className="ml-1 opacity-70">{count}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Summary strip */}
      <div className="mb-4 flex flex-wrap gap-4 border-b-[0.5px] border-b-[var(--rule)] pb-4">
        {(['show', 'require', 'hide', 'optional'] as const).map((eff) => (
          <div
            key={eff}
            className="flex items-center gap-1.5 text-[11px] font-light text-[var(--ink-3)]"
          >
            <span
              className={cn('h-[7px] w-[7px] rounded-full', EFFECT_DOT[eff])}
            />
            <span className="font-normal text-[var(--ink-2)]">
              {effectCounts[eff] ?? 0}
            </span>
            {EFFECT_CONFIG[eff]?.label.toLowerCase()}
          </div>
        ))}
      </div>

      {/* Grouped accordions */}
      <div className="flex flex-col gap-2.5">
        {groups.map((group) => {
          const isExpanded = expandedGroups.has(group.id)
          const Icon = group.icon
          const groupEffects = new Set(group.rules.map((r) => r.effect))

          return (
            <div key={group.id}>
              {/* Group header */}
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                className={cn(
                  'flex w-full cursor-pointer items-center justify-between rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3.5 py-2.5 transition-all duration-150 hover:bg-[var(--bg-subtle)]',
                  isExpanded && 'rounded-b-none border-b-transparent',
                )}
              >
                <div className="flex items-center gap-2.5">
                  <ChevronDown
                    size={15}
                    className={cn(
                      'text-[var(--ink-3)] transition-transform duration-200',
                      !isExpanded && '-rotate-90',
                    )}
                  />
                  <div
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-md',
                      group.iconClass,
                    )}
                  >
                    <Icon size={14} />
                  </div>
                  <span className="text-[13px] font-normal text-[var(--ink)]">
                    {group.label}
                  </span>
                  <span className="rounded-[10px] bg-[var(--bg-subtle)] px-2.5 py-0.5 text-[10.4px] font-light text-[var(--ink-3)]">
                    {group.rules.length}{' '}
                    {group.rules.length === 1 ? 'rule' : 'rules'}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {['show', 'require', 'hide', 'optional'].map(
                    (eff) =>
                      groupEffects.has(eff as FieldVisibilityEffect) && (
                        <span
                          key={eff}
                          className={cn(
                            'h-[5px] w-[5px] rounded-full',
                            EFFECT_DOT[eff],
                          )}
                        />
                      ),
                  )}
                </div>
              </button>

              {/* Rules list by effect */}
              {isExpanded && (
                <div className="overflow-hidden rounded-b-lg border-x-[0.5px] border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
                  <EffectSubgroups
                    rules={group.rules}
                    onEdit={openEdit}
                    onDelete={setDeleting}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* No results after filter */}
      {groups.length === 0 && (search || filter !== 'all') && (
        <div className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-6 text-center text-[12px] text-[var(--ink-3)]">
          No rules match your filter. Try a different search or filter.
        </div>
      )}

      <FieldVisibilityRuleDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        rule={editing}
      />
      <ConfirmationDialog
        open={!!deleting}
        onOpenChange={(next) => {
          if (!next) setDeleting(null)
        }}
        title={`Delete ${deleting?.name ?? 'rule'}?`}
        description="This removes the field visibility rule from future registration checks."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={remove.isPending}
        onConfirm={handleDelete}
      />
    </div>
  )
}
